import Icon from '@mdi/react';
import { mdiMapMarkerRadius, mdiLayersTriple, mdiAccessPointNetwork } from '@mdi/js';

interface WhatIsSectionProps {
  isMobile: boolean;
}

const pillars = [
  {
    icon: mdiMapMarkerRadius,
    heading: 'Real places, real people',
    text: 'Content is pinned to where it happened. Explore any neighborhood, city, or event and see it through local eyes.',
  },
  {
    icon: mdiLayersTriple,
    heading: 'Layer communities',
    text: 'Join or create location-based communities — street art, local food, neighborhood news.',
  },
  {
    icon: mdiAccessPointNetwork,
    heading: 'Live and in the moment',
    text: 'Livestream to the map, share events as they unfold, discover what\'s nearby before it\'s over.',
  },
];

function WhatIsSection({ isMobile }: WhatIsSectionProps) {
  return (
    <section style={styles(isMobile).section} data-what-is-section>
      <div style={styles(isMobile).container}>
        <h1 style={styles(isMobile).heading}>
          hereabout
        </h1>
        <p style={styles(isMobile).tagline}>
          See what's <em>actually</em> happening.
        </p>
        <p style={styles(isMobile).lede}>
          A social network built on the map. Every photo, video, and livestream is tied to a real place, straight from the camera. <span style={{ fontWeight: 500 }}>No edits, no AI.</span> See the world through the eyes of people who are actually there.
        </p>
        <div style={styles(isMobile).pillars}>
          {pillars.map((pillar) => (
            <div key={pillar.heading} style={styles(isMobile).card}>
              <div style={styles(isMobile).iconContainer}>
                <Icon path={pillar.icon} size={1.4} color="#dc2626" />
              </div>
              <h2 style={styles(isMobile).cardHeading}>{pillar.heading}</h2>
              <p style={styles(isMobile).cardText}>{pillar.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default WhatIsSection;

const styles = (isMobile: boolean) => ({
  section: {
    padding: isMobile ? '60px 20px' : '80px 20px',
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
  },

  container: {
    maxWidth: '900px',
    margin: '0 auto',
  },

  heading: {
    fontSize: isMobile ? '2.5rem' : '3.5rem',
    fontWeight: '400' as const,
    color: '#1e293b',
    margin: '0 0 24px 0',
    lineHeight: '1.2',
    letterSpacing: '-0.02em',
    fontFamily: '"Maven Pro", sans-serif',
    textAlign: 'center' as const,
  },

  tagline: {
    fontSize: isMobile ? '1.3rem' : '1.5rem',
    fontWeight: '700' as const,
    color: '#fc2c03',
    margin: '0 0 24px 0',
    textAlign: 'center' as const,
    letterSpacing: '-0.01em',
  },

  lede: {
    fontSize: isMobile ? '1.05rem' : '1.2rem',
    lineHeight: '1.7',
    color: '#475569',
    margin: '0 auto 48px auto',
    textAlign: 'center' as const,
    maxWidth: '600px',
  },

  pillars: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
    gap: isMobile ? '16px' : '24px',
  },

  card: {
    background: '#ffffff',
    borderRadius: '16px',
    padding: '32px 24px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.03)',
    transition: 'transform 200ms cubic-bezier(0.25, 1, 0.5, 1), box-shadow 200ms cubic-bezier(0.25, 1, 0.5, 1)',
    textAlign: 'center' as const,
  },

  iconContainer: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
  },

  cardHeading: {
    fontSize: '1.1rem',
    fontWeight: '700' as const,
    color: '#1e293b',
    margin: '0 0 8px 0',
    letterSpacing: '-0.01em',
  },

  cardText: {
    fontSize: '0.95rem',
    lineHeight: '1.6',
    color: '#64748b',
    margin: '0',
  },
});
